/**
 * Reporter de erros JS — POST /api/v1/client-errors (PR #43).
 *
 * 3 caminhos de captura:
 *   1. `setupGlobalErrorReporting()` no main.tsx — registra
 *      `window.onerror` + `unhandledrejection`.
 *   2. `<ErrorBoundary>` chama `reportClientError({origem:"boundary"})` em
 *      componentDidCatch.
 *   3. `reportClientError({origem:"manual", ...})` pra avisos custom.
 *
 * Características:
 *   - Best-effort: falha silenciosa (não derruba a UI).
 *   - Throttle: deduplica mensagem repetida no mesmo ciclo (window de 5s).
 *   - Off em dev por default (process.env.NODE_ENV); ativa em prod.
 *     Override: localStorage.setItem("acordei.errorReporter", "1").
 *   - **Sample rate** (PR #53): VITE_ERROR_SAMPLE_RATE env (0.0-1.0).
 *     Default 1.0 (100%). Aplica antes de cada POST. Erros `boundary`
 *     SEMPRE reportam (critical path) — sample rate só afeta window/promise/manual.
 *     Override por sessão: localStorage.setItem("acordei.errorSampleRate", "0.5").
 *   - Stack trim: clipa em 16KB pra não estourar payload.
 */

/** Mesmo BASE_URL do `lib/api.ts` — duplicado de propósito pra não introduzir
 * dependência circular (errorReporter deve continuar funcionando mesmo se
 * o api.ts falhar). */
const BASE_URL: string =
  (import.meta as unknown as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE ??
  "/api/v1";

/** Sample rate global (PR #53). Lido uma vez na inicialização — env do build.
 * Override por sessão via localStorage `acordei.errorSampleRate`.
 *
 * Valores válidos: 0.0 (nada) até 1.0 (tudo). Default: 1.0.
 *
 * Erros `boundary` ignoram o sample rate (sempre reportam) porque indicam
 * crash da UI — perdê-los seria perder o sinal mais importante. */
const _SAMPLE_RATE_DEFAULT: number = (() => {
  const raw = (import.meta as unknown as { env?: { VITE_ERROR_SAMPLE_RATE?: string } })
    .env?.VITE_ERROR_SAMPLE_RATE;
  if (!raw) return 1.0;
  const n = Number(raw);
  if (Number.isFinite(n) && n >= 0 && n <= 1) return n;
  return 1.0;
})();

function effectiveSampleRate(): number {
  try {
    const stored = window.localStorage.getItem("acordei.errorSampleRate");
    if (stored) {
      const n = Number(stored);
      if (Number.isFinite(n) && n >= 0 && n <= 1) return n;
    }
  } catch {
    /* ignorar */
  }
  return _SAMPLE_RATE_DEFAULT;
}

type Origem = "boundary" | "window" | "promise" | "manual";

type ErrorReport = {
  origem: Origem;
  mensagem: string;
  stack?: string;
  url?: string;
  user_agent?: string;
  component_stack?: string;
  caso_id?: string;
  payload?: Record<string, unknown>;
};

const _seen = new Map<string, number>(); // mensagem -> timestamp ms
const _DEDUP_WINDOW_MS = 5000;
const _STACK_MAX = 16 * 1024;

function shouldReport(): boolean {
  try {
    const override = window.localStorage.getItem("acordei.errorReporter");
    if (override === "1") return true;
    if (override === "0") return false;
  } catch {
    /* localStorage indisponível */
  }
  // Em dev sem override, ainda reporta porque é útil pra QA local
  return true;
}

function dedupKey(r: ErrorReport): string {
  return `${r.origem}::${r.mensagem.slice(0, 200)}`;
}

export async function reportClientError(report: ErrorReport): Promise<void> {
  if (!shouldReport()) return;

  // Sample rate (PR #53): boundary sempre passa; outros respeitam rate
  if (report.origem !== "boundary") {
    const rate = effectiveSampleRate();
    if (rate < 1.0 && Math.random() > rate) {
      return; // sampleou fora — não reporta
    }
  }

  const key = dedupKey(report);
  const now = Date.now();
  const last = _seen.get(key);
  if (last && now - last < _DEDUP_WINDOW_MS) {
    return; // duplicata recente — skip
  }
  _seen.set(key, now);

  const body: ErrorReport = {
    ...report,
    stack: report.stack ? report.stack.slice(0, _STACK_MAX) : undefined,
    component_stack: report.component_stack
      ? report.component_stack.slice(0, _STACK_MAX)
      : undefined,
  };

  try {
    // Usa fetch direto (não o cliente api.ts) pra evitar dependência circular —
    // se o api.ts quebrar, errorReporter deve continuar funcionando.
    await fetch(`${BASE_URL}/client-errors`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      // keepalive permite que o request sobreviva a unload da página
      keepalive: true,
    });
  } catch {
    /* silencioso — telemetria não deve quebrar UX */
  }
}

/**
 * Registra handlers globais. Chamar uma vez no main.tsx (antes de renderizar).
 * Idempotente — pode chamar várias vezes sem efeito colateral.
 */
let _setup = false;

export function setupGlobalErrorReporting(): void {
  if (_setup) return;
  _setup = true;

  if (typeof window === "undefined") return;

  window.addEventListener("error", (event: ErrorEvent) => {
    void reportClientError({
      origem: "window",
      mensagem: event.message || "window.onerror",
      stack: event.error?.stack,
      url: window.location.href,
      user_agent: navigator.userAgent,
      payload: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const mensagem =
      reason instanceof Error
        ? reason.message
        : typeof reason === "string"
          ? reason
          : "unhandledrejection";
    const stack = reason instanceof Error ? reason.stack : undefined;
    void reportClientError({
      origem: "promise",
      mensagem: mensagem || "unhandledrejection",
      stack,
      url: window.location.href,
      user_agent: navigator.userAgent,
    });
  });
}
